import React, { useState } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import ArticleCardBase from './ArticleCardBase';
import ActionButtons from '../../molecules/ActionButtons/ActionButtons';
import OverlayIcons from '../../molecules/OverlayIcons/OverlayIcons';
import withArticleTags from '../../hoc/withArticleTags';
import { articleCardPropTypes, articleCardDefaultProps } from './ArticleCardProps';
import useCardBehavior from './useCardBehavior';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityIcon from '@mui/icons-material/Visibility';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import LabelIcon from '@mui/icons-material/Label';
import CollectionsIcon from '@mui/icons-material/Collections';
import EmailIcon from '@mui/icons-material/Email';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { isQualitativeAnalysisAiApplied } from '../../../utils/aiAnalysisStatus';
import BottomSheetHandle from '../../atoms/BottomSheetHandle';

/**
 * ArticleCardDetailed - Versão detalhada do card de artigo
 * Usa ArticleCardBase e adiciona overlays e ActionButtons específicos.
 *
 * TRANSFORMER PATTERN: Usa interface e comportamento compartilhados.
 * Qualquer nova funcionalidade adicionada ao sistema automaticamente funciona aqui.
 */
const ArticleCardDetailed = ({
  article,
  onArticleClick,
  onAction,
  onToggleFavorite,
  onToggleRead,
  onUpdateSentiment,
  onEdit,
  onAddTag,
  onAddToCollection,
  onDelete,
  selectionMode = false,
  isSelected = false,
  onSelect,
  onSendNewsletter,
  className = '',
  onTagRemove,
  onTagClick,
  hideHoverActions = false,
  onRescrapeSmart,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileActionsOpen, setMobileActionsOpen] = useState(false);
  const { renderTags, ...cardProps } = props;
  const imageChromeHeight = 0;

  // Hook compartilhado - lógica comum para todas as variantes
  const {
    hover,
    handleAction,
    handleMouseEnter,
    handleMouseLeave
  } = useCardBehavior({
    article,
    selectionMode,
    onSelect,
    onArticleClick,
    onToggleFavorite,
    onToggleRead,
    onEdit,
    onAddTag,
    onAddToCollection,
    onDelete,
    onSendNewsletter,
    onRescrapeSmart
  });

  // Overlay da imagem: OverlayIcons (sempre visível) + ActionButtons completo (só no hover)
  const handleOpenOriginal = (event) => {
    event.stopPropagation();
    const originalUrl = article.siteUrl || article.url;
    if (originalUrl) {
      window.open(originalUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const runMobileAction = (action) => {
    setMobileActionsOpen(false);
    action();
  };

  const mobileActions = [
    {
      label: 'Abrir link original',
      icon: <OpenInNewIcon />,
      disabled: !(article.siteUrl || article.url),
      action: () => handleOpenOriginal({ stopPropagation: () => {} })
    },
    {
      label: 'Visualizar publicação',
      icon: <VisibilityIcon />,
      action: () => onArticleClick && onArticleClick(article)
    },
    {
      label: article.isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos',
      icon: article.isFavorite ? <StarIcon /> : <StarBorderIcon />,
      action: () => handleAction('toggleFavorite', article)
    },
    {
      label: 'Alterar sentimento',
      icon: <SentimentSatisfiedIcon />,
      action: () => onUpdateSentiment && onUpdateSentiment(
        article._id,
        article.sentiment === 'positive' ? 'neutral' : article.sentiment === 'neutral' ? 'negative' : 'positive'
      )
    },
    { label: 'Gerenciar tags', icon: <LabelIcon />, action: () => handleAction('addTag', article) },
    { label: 'Adicionar à coleção', icon: <CollectionsIcon />, action: () => handleAction('addCollection', article) },
    { label: 'Enviar como newsletter', icon: <EmailIcon />, action: () => handleAction('sendNewsletter', article) },
    { label: 'Editar publicação', icon: <EditIcon />, action: () => handleAction('edit', article) },
    { label: 'Excluir publicação', icon: <DeleteIcon />, color: 'error', action: () => handleAction('delete', article) }
  ];

  const renderImageOverlay = () => (
    <>
      {!isMobile && <OverlayIcons
        isFavorite={article.isFavorite}
        isRead={article.isRead}
        sentiment={article.sentiment}
        mediaType={article.mediaType}
        qualitativeAnalysisApplied={isQualitativeAnalysisAiApplied(article)}
        onToggleFavorite={onAction ? (e) => { e.stopPropagation(); onAction('toggleFavorite', article); } : undefined}
        onToggleRead={onAction ? (e) => { e.stopPropagation(); onAction('toggleRead', article); } : undefined}
        onUpdateSentiment={onUpdateSentiment ? (e) => { e.stopPropagation(); onUpdateSentiment(article._id, article.sentiment === 'positive' ? 'neutral' : article.sentiment === 'neutral' ? 'negative' : 'positive'); } : undefined}
      />}
      {/* Botões de ação completos — centralizados vertical e horizontalmente sobre a foto */}
      <div
        style={{
          position: 'absolute',
          top: imageChromeHeight,
          left: 0,
          width: '100%',
          height: `calc(100% - ${imageChromeHeight}px)`,
          background: 'rgba(15, 17, 21, 0.58)',
          display: isMobile ? 'none' : 'block',
          opacity: hover ? 1 : 0,
          transition: 'opacity 0.16s ease',
          pointerEvents: hover ? 'auto' : 'none',
          overflow: 'hidden',
        }}
      >
        {/* Link principal centralizado sobre a imagem */}
        <div style={{
          position: 'absolute',
          top: '48%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          justifyContent: 'center',
          width: '100%'
        }}>
          <button
            type="button"
            onClick={handleOpenOriginal}
            disabled={!(article.siteUrl || article.url)}
            aria-label="Abrir link original"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              border: 0,
              background: 'rgba(32, 35, 40, 0.94)',
              borderRadius: 999,
              color: 'rgba(248, 250, 252, 0.94)',
              cursor: article.siteUrl || article.url ? 'pointer' : 'default',
              fontSize: 12,
              fontWeight: 760,
              letterSpacing: 0,
              lineHeight: 1,
              textTransform: 'none',
              padding: '9px 14px',
            }}
          >
            Link original
            <OpenInNewIcon sx={{ fontSize: 15, color: 'currentColor' }} />
          </button>
        </div>

        {/* Bloco de ícones de ação posicionado abaixo do centro */}
        <div style={{ 
          position: 'absolute',
          top: 'calc(48% + 42px)',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 'calc(100% - 32px)',
          maxWidth: 300,
          display: hideHoverActions ? 'none' : 'flex',
          justifyContent: 'center',
          overflow: 'hidden',
        }}>
          <ActionButtons
            article={article}
            onAction={handleAction}
            onUpdateSentiment={onUpdateSentiment}
            glassmorphismOverlay={false}
            iconColor="rgba(248, 250, 252, 0.92)"
            favoriteActiveColor="rgba(248, 250, 252, 0.92)"
            showOpenOriginal={false}
            buttonSx={{
              width: 24,
              height: 24,
              minWidth: 24,
              background: 'rgba(32, 35, 40, 0.86)',
              p: 0.1,
              '&:hover': {
                background: 'rgba(46, 50, 57, 0.96)'
              },
              '& .MuiSvgIcon-root': {
                fontSize: '15px !important'
              }
            }}
            showMoreOptions
          />
        </div>
      </div>
      {isMobile && (
        <IconButton
          aria-label="Abrir ações da publicação"
          onClick={(event) => {
            event.stopPropagation();
            setMobileActionsOpen(true);
          }}
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            zIndex: 4,
            width: 34,
            height: 34,
            color: '#fff',
            bgcolor: 'rgba(20, 22, 27, 0.82)',
            border: '1px solid rgba(255,255,255,0.18)',
            '&:hover': { bgcolor: 'rgba(20, 22, 27, 0.82)' }
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}
    </>
  );

  return (
    <div
      className={`article-card-detailed-wrapper ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: 'relative',
        cursor: selectionMode ? 'pointer' : 'default',
      }}
    >
      <ArticleCardBase
        article={article}
        variant="detailed"
        onArticleClick={onArticleClick}
        onToggleFavorite={onToggleFavorite}
        onToggleRead={onToggleRead}
        onUpdateSentiment={onUpdateSentiment}
        onEdit={onEdit}
        onAddTag={onAddTag}
        selectionMode={selectionMode}
        isSelected={isSelected}
        onSelect={onSelect}
        className={className}
        actions={[]}
        imageOverlay={renderImageOverlay}
        footerActions={null}
        onTagRemove={onTagRemove}
        onTagClick={onTagClick}
        {...cardProps}
      />
      <Drawer
        anchor="bottom"
        open={mobileActionsOpen}
        onClose={() => setMobileActionsOpen(false)}
        PaperProps={{ sx: { borderRadius: '20px 20px 0 0', maxHeight: '82vh' } }}
      >
        <Box onClick={(event) => event.stopPropagation()} sx={{ pb: 'max(12px, env(safe-area-inset-bottom))' }}>
          <BottomSheetHandle sx={{ mt: 1, mb: 1.5 }} />
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Typography variant="h6" fontWeight={800}>Ações da publicação</Typography>
            <Typography variant="body2" color="text.secondary" noWrap>{article.title}</Typography>
          </Box>
          <List disablePadding sx={{ overflowY: 'auto' }}>
            {mobileActions.map(item => (
              <ListItemButton
                key={item.label}
                disabled={item.disabled}
                onClick={() => runMobileAction(item.action)}
                sx={{ px: 2.5, py: 1.25, color: item.color === 'error' ? 'error.main' : 'text.primary' }}
              >
                <ListItemIcon sx={{ minWidth: 42, color: 'inherit' }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: item.color === 'error' ? 700 : 600 }} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>
    </div>
  );
};

// Usa a interface compartilhada - única fonte da verdade
ArticleCardDetailed.propTypes = articleCardPropTypes;
ArticleCardDetailed.defaultProps = articleCardDefaultProps;

// Memoizar para evitar re-renders desnecessários quando outros cards são selecionados
export default React.memo(withArticleTags(ArticleCardDetailed, true));
